import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, Users, Camera,
  Handshake, Settings, ChevronLeft, ChevronRight,
  FolderOpen, UserCheck, ShieldCheck, UserCircle,
  Package, TrendingUp, ClipboardList, Briefcase,
  Sun, Moon, Truck,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { Avatar } from '../ui/Avatar';
import { usePermissions } from '../../hooks/usePermission';

interface NavItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  permKey?: string;
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
}

export function Sidebar({ collapsed, onToggle, theme, onToggleTheme }: SidebarProps) {
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  const isAdmin = user?.role === 'ADMIN';
  const w = collapsed ? 60 : 260;

  const { hasPermission } = usePermissions();
  const canDashboard = hasPermission('dashboard.view');
  const canCalendar = hasPermission('calendar.view');
  const canClients = hasPermission('clients.view');
  const canEvents = hasPermission('events.view');
  const canCollaborators = hasPermission('collaborators.view');
  const canProjects = hasPermission('projects.view');
  const canExternalPhotographers = hasPermission('external-photographers.view');
  const canPerformancesView = hasPermission('performances.view');
  const canPerformancesMyView = hasPermission('performances.my.view');
  const canPerformances = canPerformancesView || canPerformancesMyView;
  const canCatalog = hasPermission('catalog.view');
  const canSuppliers = hasPermission('suppliers.view');
  const canSettings = hasPermission('settings.view');
  const canFinance = hasPermission('finance.view');
  const canUsersManage = hasPermission('users.manage');

  const navGroups: { items: NavItem[] }[] = [
    {
      items: [
        ...(canDashboard ? [{ to: '/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard' }] : []),
        ...(canFinance ? [{ to: '/finance', icon: <TrendingUp size={18} />, label: 'Finanza' }] : []),
      ],
    },
    {
      items: [
        { to: '/tasks', icon: <ClipboardList size={18} />, label: 'Assegnazioni' },
        ...(canCalendar ? [{ to: '/calendar', icon: <Calendar size={18} />, label: 'Calendario' }] : []),
        ...(canEvents ? [{ to: '/events', icon: <Camera size={18} />, label: 'Eventi' }] : []),
        ...(canPerformances ? [{ to: '/performances', icon: <Briefcase size={18} />, label: 'Prestazioni' }] : []),
      ],
    },
    {
      items: [
        ...(canProjects ? [{ to: '/projects', icon: <FolderOpen size={18} />, label: 'Progetti' }] : []),
        ...(canCatalog ? [{ to: '/catalog', icon: <Package size={18} />, label: 'Catalogo' }] : []),
        ...(canSuppliers ? [{ to: '/suppliers', icon: <Truck size={18} />, label: 'Fornitori' }] : []),
      ],
    },
    {
      items: [
        ...(canClients ? [{ to: '/clients', icon: <Users size={18} />, label: 'Clienti' }] : []),
        ...(canExternalPhotographers ? [{ to: '/external-photographers', icon: <UserCheck size={18} />, label: 'Esterni' }] : []),
        ...(canCollaborators ? [{ to: '/collaborators', icon: <Handshake size={18} />, label: 'Collaboratori' }] : []),
      ],
    },
    {
      items: [
        { to: '/profile', icon: <UserCircle size={18} />, label: 'Profilo' },
        ...(canUsersManage ? [{ to: '/users', icon: <ShieldCheck size={18} />, label: 'Gestione Utenti' }] : []),
        ...(canSettings ? [{ to: '/settings', icon: <Settings size={18} />, label: 'Impostazioni' }] : []),
      ],
    },
  ];

  return (
    <aside className="sidebar-glass" style={{
      width: w,
      minWidth: w,
      height: '100dvh',
      background: 'var(--glass-bg-sidebar)',
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      borderRight: '0.5px solid rgba(255,255,255,0.08)',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      flexShrink: 0,
      willChange: 'width',
    }}>
      {/* Logo */}
      <div style={{
        height: 'var(--topbar-height)',
        display: 'flex',
        alignItems: 'center',
        padding: collapsed ? '0 0.875rem' : '0 1.25rem',
        borderBottom: '0.5px solid rgba(255,255,255,0.06)',
        gap: '0.75rem',
        flexShrink: 0,
      }}>
        <LCLogo size={28} />
        {!collapsed && (
          <span style={{
            fontFamily: 'Satoshi, system-ui, sans-serif', fontWeight: 700,
            fontSize: '1.25rem',
            color: 'var(--color-text)',
            whiteSpace: 'nowrap',
          }}>
            LensCRM
          </span>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '0.75rem 0', overflowY: 'auto', transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)', willChange: 'width, backdrop-filter' }} aria-label="Navigazione principale">
        {navGroups.filter(g => g.items.length > 0).map((group, gi) => (
          <div key={gi}>
            {gi > 0 && (
              <div style={{
                height: '0.5px',
                background: 'rgba(255,255,255,0.06)',
                margin: '4px 12px',
              }} />
            )}
            {group.items.map(item => {
              const active = location.pathname.startsWith(item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={collapsed ? item.label : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: collapsed
                      ? '0.625rem 0.875rem 0.625rem calc(0.875rem - 3px)'
                      : '0.625rem 1.25rem 0.625rem calc(1.25rem - 3px)',
                    margin: '0.125rem 0.5rem',
                    borderRadius: '0 8px 8px 0',
                    borderLeft: active ? '3px solid #3E91FF' : '3px solid transparent',
                    color: active ? '#3E91FF' : 'var(--color-text-muted)',
                    background: active
                      ? 'linear-gradient(to right, rgba(62,145,255,0.12), rgba(62,145,255,0.06))'
                      : 'transparent',
                    fontSize: 'var(--text-sm)',
                    fontWeight: active ? 600 : 400,
                    textDecoration: 'none',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => {
                    if (!active) {
                      (e.currentTarget as HTMLElement).style.background = collapsed
                        ? 'var(--glass-bg-elevated)'
                        : 'var(--glass-bg-card)';
                      (e.currentTarget as HTMLElement).style.color = 'var(--color-text)';
                      if (collapsed) {
                        (e.currentTarget as HTMLElement).style.backdropFilter = 'blur(12px)';
                        ((e.currentTarget as HTMLElement).style as any)['WebkitBackdropFilter'] = 'blur(12px)';
                        (e.currentTarget as HTMLElement).style.borderRadius = 'var(--radius-full)';
                      }
                    }
                  }}
                  onMouseLeave={e => {
                    if (!active) {
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                      (e.currentTarget as HTMLElement).style.color = 'var(--color-text-muted)';
                      (e.currentTarget as HTMLElement).style.backdropFilter = '';
                      ((e.currentTarget as HTMLElement).style as any)['WebkitBackdropFilter'] = '';
                      (e.currentTarget as HTMLElement).style.borderRadius = '';
                    }
                  }}
                >
                  {item.to === '/profile'
                    ? <Avatar src={user?.avatarUrl} name={user?.name} size={18} />
                    : item.icon
                  }
                  {!collapsed && item.label}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Theme toggle */}
      <div style={{
        padding: '0 0.75rem 0.375rem 0.75rem',
      }}>
        <button
          onClick={onToggleTheme}
          title={theme === 'dark' ? 'Passa a chiaro' : 'Passa a scuro'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            width: '100%',
            background: 'var(--glass-bg-card)',
            border: 'var(--glass-border)',
            borderRadius: 'var(--radius-interactive)',
            color: 'var(--color-text-muted)',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
            fontSize: 'var(--text-sm)',
            transition: 'all var(--spring-duration) var(--spring-easing)',
            ...(collapsed ? { justifyContent: 'center', padding: '0.5rem' } : {}),
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(62,145,255,0.10)';
            e.currentTarget.style.color = '#3E91FF';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--glass-bg-card)';
            e.currentTarget.style.color = 'var(--color-text-muted)';
          }}
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          {!collapsed && (theme === 'dark' ? 'Tema chiaro' : 'Tema scuro')}
        </button>
      </div>

      {/* Collapse toggle */}
      <div style={{
        padding: '0.75rem',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        justifyContent: collapsed ? 'center' : 'flex-end',
      }}>
        <button
          onClick={onToggle}
          aria-label={collapsed ? 'Espandi sidebar' : 'Comprimi sidebar'}
          style={{
            background: 'none',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-interactive)',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            padding: '0.25rem',
            display: 'flex',
            alignItems: 'center',
            transition: 'all var(--transition-interactive)',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.color = 'var(--color-text)';
            (e.currentTarget as HTMLElement).style.background = 'var(--glass-bg-card)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.color = 'var(--color-text-muted)';
            (e.currentTarget as HTMLElement).style.background = 'none';
          }}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </aside>
  );
}

function LCLogo({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="28" height="28" rx="7" fill="var(--color-primary)" />
      <text x="4" y="21" fontFamily="Satoshi, system-ui, sans-serif" fontWeight="700" fontSize="18" fill="white">LC</text>
    </svg>
  );
}
